(() => {
  if (window.__tramasQuickAddInitialized) return;

  window.__tramasQuickAddInitialized = true;

  const SELECTORS = {
    card: '[data-tramas-qa-card]',
    trigger: '[data-tramas-qa-trigger]',
    panel: '[data-tramas-qa-panel]',
    variant: '[data-tramas-qa-variant]',
    mobileRoot: '[data-tramas-qa-mobile]',
    mobileOverlay: '[data-tramas-qa-mobile-overlay]',
    mobileInner: '[data-tramas-qa-mobile-inner]',
    mobileContent: '[data-tramas-qa-mobile-content]',
  };

  const DESKTOP_QUERY = '(min-width: 990px) and (any-hover: hover) and (any-pointer: fine)';
  const desktopMediaQuery = window.matchMedia(DESKTOP_QUERY);

  let activeTrigger = null;
  let closeTimer = null;
  let addInProgress = false;

  const isDesktopMode = () => desktopMediaQuery.matches;

  const getRoute = (name, fallback) => {
    if (window.FoxTheme?.routes?.[name]) {
      return window.FoxTheme.routes[name];
    }

    const root = window.Shopify?.routes?.root || '/';
    return `${root}${fallback}`.replace(/\/+/g, '/');
  };

  const getEventTarget = (event) => {
    return event.target instanceof Element ? event.target : null;
  };

  const getMobileRoot = () => document.querySelector(SELECTORS.mobileRoot);

  const getPositiveInteger = (value, fallback = null) => {
    const parsedValue = Number.parseInt(String(value), 10);

    if (!Number.isSafeInteger(parsedValue) || parsedValue < 1) {
      return fallback;
    }

    return parsedValue;
  };

  const dispatchThemeEvent = (name, detail) => {
    document.dispatchEvent(
      new CustomEvent(name, {
        bubbles: true,
        detail,
      })
    );
  };

  const closeAllDesktopPanels = () => {
    document.querySelectorAll(SELECTORS.panel).forEach((panel) => {
      panel.classList.remove('is-active');
    });

    document.querySelectorAll(SELECTORS.trigger).forEach((trigger) => {
      trigger.classList.remove('is-active');
      trigger.setAttribute('aria-expanded', 'false');
    });
  };

  const closeMobilePanel = ({ restoreFocus = true } = {}) => {
    const root = getMobileRoot();

    if (!root || root.hidden) return;

    const content = root.querySelector(SELECTORS.mobileContent);
    const triggerToRestore = activeTrigger;

    if (closeTimer) {
      window.clearTimeout(closeTimer);
      closeTimer = null;
    }

    root.classList.remove('is-active');
    root.classList.add('is-closing');
    root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('tramas-qa-mobile-open');

    closeTimer = window.setTimeout(() => {
      root.classList.remove('is-closing');
      root.hidden = true;

      if (content) {
        content.replaceChildren();
      }

      if (restoreFocus && triggerToRestore?.isConnected) {
        triggerToRestore.focus({ preventScroll: true });
      }

      activeTrigger = null;
      closeTimer = null;
    }, 260);
  };

  const openMobilePanel = (card, trigger) => {
    const root = getMobileRoot();
    const panelContent = card.querySelector(
      `${SELECTORS.panel} .tramas-qa-panel-content`
    );

    if (!root || !panelContent) return;

    const content = root.querySelector(SELECTORS.mobileContent);

    if (!content) return;

    if (closeTimer) {
      window.clearTimeout(closeTimer);
      closeTimer = null;
    }

    closeAllDesktopPanels();

    content.replaceChildren(panelContent.cloneNode(true));
    activeTrigger = trigger;

    root.hidden = false;
    root.classList.remove('is-closing');
    root.setAttribute('aria-hidden', 'false');

    requestAnimationFrame(() => {
      root.classList.add('is-active');
      document.body.classList.add('tramas-qa-mobile-open');

      const firstFocusable = root.querySelector(SELECTORS.variant);

      firstFocusable?.focus({ preventScroll: true });
    });
  };

  const openDesktopPanel = (card, trigger) => {
    const panel = card.querySelector(SELECTORS.panel);

    if (!panel) return;

    const wasOpen = panel.classList.contains('is-active');

    closeMobilePanel({ restoreFocus: false });
    closeAllDesktopPanels();

    if (wasOpen) return;

    panel.classList.add('is-active');
    trigger.classList.add('is-active');
    trigger.setAttribute('aria-expanded', 'true');
  };

  const setLoadingState = (element, loading) => {
    if (!element) return;

    element.classList.toggle('is-loading', loading);

    if (loading) {
      element.setAttribute('aria-busy', 'true');
    } else {
      element.removeAttribute('aria-busy');
    }
  };

  const addVariantToCart = async (variantIdValue, quantityValue, sourceElement) => {
    if (addInProgress || !sourceElement) return;

    let errorEventDispatched = false;

    const variantId = getPositiveInteger(variantIdValue);
    const quantity = getPositiveInteger(quantityValue, 1);
    const productUrl =
      sourceElement.dataset.productUrl ||
      sourceElement.closest(SELECTORS.card)?.dataset.productUrl ||
      '';

    if (!variantId) {
      if (productUrl) window.location.assign(productUrl);
      return;
    }

    addInProgress = true;
    setLoadingState(sourceElement, true);

    try {
      const response = await fetch(getRoute('cart_add_url', 'cart/add.js'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity,
            },
          ],
        }),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        const errorMessage =
          result.description ||
          result.message ||
          'No se ha podido añadir la variante al carrito.';

        dispatchThemeEvent('product-ajax:error', {
          errorMessage,
        });
        errorEventDispatched = true;

        throw new Error(errorMessage);
      }

      const addedProduct = Array.isArray(result.items) ? result.items[0] : result;

      dispatchThemeEvent('product-ajax:added', {
        product: addedProduct,
      });

      closeAllDesktopPanels();
      closeMobilePanel({ restoreFocus: false });

      dispatchThemeEvent('cart:refresh', {
        open: true,
      });
    } catch (error) {
      if (!errorEventDispatched) {
        dispatchThemeEvent('product-ajax:error', {
          errorMessage:
            error instanceof Error
              ? error.message
              : 'No se ha podido añadir la variante al carrito.',
        });
      }

      console.error('Error añadiendo la variante al carrito:', error);

      if (productUrl) {
        window.location.assign(productUrl);
      }
    } finally {
      setLoadingState(sourceElement, false);
      addInProgress = false;
    }
  };

  const trapMobileFocus = (event) => {
    if (event.key !== 'Tab') return;

    const root = getMobileRoot();

    if (!root || root.hidden || !root.classList.contains('is-active')) return;

    const focusableElements = Array.from(
      root.querySelectorAll(
        'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => !element.hasAttribute('hidden'));

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
      return;
    }

    if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  };

  document.addEventListener(
    'pointerdown',
    (event) => {
      const target = getEventTarget(event);
      const root = getMobileRoot();

      if (
        !target ||
        !root ||
        root.hidden ||
        !root.classList.contains('is-active')
      ) {
        return;
      }

      const inner = root.querySelector(SELECTORS.mobileInner);

      if (!inner || inner.contains(target)) return;

      event.preventDefault();
      event.stopPropagation();
      closeMobilePanel();
    },
    true
  );

  document.addEventListener('click', (event) => {
    const target = getEventTarget(event);

    if (!target) return;

    const variantButton = target.closest(SELECTORS.variant);

    if (variantButton) {
      event.preventDefault();

      addVariantToCart(
        variantButton.dataset.variantId,
        variantButton.dataset.quantity || 1,
        variantButton
      );
      return;
    }

    const trigger = target.closest(SELECTORS.trigger);

    if (trigger) {
      event.preventDefault();

      const card = trigger.closest(SELECTORS.card);
      const productUrl = trigger.dataset.productUrl || card?.dataset.productUrl || '';
      const productAvailable = trigger.dataset.productAvailable === 'true';

      if (!productAvailable) {
        if (productUrl) window.location.assign(productUrl);
        return;
      }

      if (!card) return;

      if (isDesktopMode()) {
        openDesktopPanel(card, trigger);
      } else {
        openMobilePanel(card, trigger);
      }

      return;
    }

    if (!target.closest(SELECTORS.card) && !target.closest(SELECTORS.mobileRoot)) {
      closeAllDesktopPanels();
    }
  });

  document.addEventListener('keydown', (event) => {
    trapMobileFocus(event);

    if (event.key !== 'Escape') return;

    closeAllDesktopPanels();
    closeMobilePanel();
  });

  const handleInteractionModeChange = () => {
    closeAllDesktopPanels();
    closeMobilePanel({ restoreFocus: false });
  };

  if (typeof desktopMediaQuery.addEventListener === 'function') {
    desktopMediaQuery.addEventListener('change', handleInteractionModeChange);
  } else {
    desktopMediaQuery.addListener(handleInteractionModeChange);
  }
})();
